import { Fragment } from "react";
import { Link } from "wouter";
import { CornerMarks, DotMatrixAccent } from "@/components/primitives";
import { BlurFade } from "@/components/ui/blur-fade";
import { GridPattern } from "@/components/ui/grid-pattern";
import { cn } from "@/lib/utils";

/* ── Pillar data ─────────────────────────────────────────────── */

interface PillarLink {
  label: string;
  href: string;
}

interface PillarLogo {
  src: string;
  alt: string;
}

interface Pillar {
  number: string;
  heading: string;
  icon: string;
  description: string;
  bullets: string[];
  links: PillarLink[];
  logos: PillarLogo[];
  linksFullWidth?: boolean;
}

const PILLARS: Pillar[] = [
  {
    number: "01",
    heading: "Performance Media",
    icon: "/images/icons/icon-riso-dashboard.webp",
    description:
      "Paid campaigns managed by strategists who've deployed over a billion dollars in ad spend. Sherpa monitors every campaign 24/7.",
    bullets: [
      "Google Ads & Shopping",
      "Meta & Instagram",
      "LinkedIn & Pinterest",
      "Display & Programmatic",
      "Retargeting",
      "Attribution modeling",
      "Measurement & reporting",
      "Google Analytics",
    ],
    links: [
      { label: "for lead gen businesses", href: "/lead-gen-services" },
      { label: "for ecommerce businesses", href: "/ecommerce" },
    ],
    linksFullWidth: true,
    logos: [
      { src: "/images/platforms/google-ads.png", alt: "Google Ads" },
      { src: "/images/platforms/meta-ads.png", alt: "Meta" },
      { src: "/images/platforms/instagram-ads.png", alt: "Instagram" },
      { src: "/images/platforms/chatgpt-ads.png", alt: "ChatGPT" },
      { src: "/images/platforms/tiktok-ads.png", alt: "TikTok" },
    ],
  },
  {
    number: "02",
    heading: "Programmatic SEO / AEO",
    icon: "/images/icons/icon-riso-data-science.webp",
    description:
      "Organic traffic engineered with technical precision. We fix the foundations most agencies skip.",
    bullets: [
      "Technical SEO audits",
      "LLM optimization & visibility",
      "LLM citation tracking",
      "Answer engine optimization",
      "Programmatic page generation",
      "Core Web Vitals",
      "Content strategy",
      "Link building",
    ],
    links: [{ label: "explore SEO", href: "/seo-agency-services" }],
    logos: [
      { src: "/images/platforms/google-ads.png", alt: "Google" },
      { src: "/images/platforms/chatgpt-ads.png", alt: "ChatGPT" },
      { src: "/images/tech-stack/claude.png", alt: "Claude" },
      { src: "/images/platforms/microsoft-ads.png", alt: "Microsoft" },
    ],
  },
  {
    number: "03",
    heading: "AI-Native Creative",
    icon: "/images/icons/icon-riso-creative-optimization-v1.webp",
    description:
      "Ad creative and landing pages produced at machine speed with human taste. Faster to make, cheaper to run, higher ROAS.",
    bullets: [
      "Video & motion ads",
      "Static creative",
      "Landing page design",
      "A/B testing & CRO",
      "UGC content",
      "Brand identity",
    ],
    links: [{ label: "explore creative", href: "/creative-services" }],
    logos: [
      { src: "/images/tech-stack/veo.png", alt: "Veo 3" },
      { src: "/images/tech-stack/higgsfield.png", alt: "Higgsfield" },
      { src: "/images/tech-stack/artlist.png", alt: "Artlist" },
      { src: "/images/tech-stack/elevenlabs.png", alt: "ElevenLabs" },
      { src: "/images/tech-stack/suno.png", alt: "Suno" },
    ],
  },
  {
    number: "04",
    heading: "AI Transformation",
    icon: "/images/icons/icon-riso-ai-brain.webp",
    description:
      "We train your team to build with AI, not just talk about it. From the agency Google chose to train its own Premier Partners.",
    bullets: [
      "Hands-on workshops",
      "Workflow audits",
      "AI tool implementation",
      "Team enablement",
      "Custom strategy",
      "Ongoing advisory",
    ],
    links: [
      { label: "events & workshops", href: "/workshops" },
      { label: "AI training for teams", href: "/ai-training-for-teams" },
      { label: "custom consulting", href: "/ai-transformation-services" },
    ],
    logos: [
      { src: "/assets/logos/logo_chatgpt.webp", alt: "OpenAI" },
      { src: "/images/tech-stack/claude.png", alt: "Anthropic" },
      { src: "/images/tech-stack/gemini.png", alt: "Gemini" },
      { src: "/images/platforms/microsoft-ads.png", alt: "Copilot" },
    ],
  },
];

/* ── Component ───────────────────────────────────────────────── */

export interface ServicesExplorerProps {
  className?: string;
}

export function ServicesExplorer({ className }: ServicesExplorerProps) {
  return (
    <section
      id="services-explorer"
      className={className}
      data-testid="section-services-explorer"
    >
      {/* Two-column grid: sticky left + scrolling right */}
      <div
        className="hidden lg:grid"
        style={{ gridTemplateColumns: "55% 45%" }}
      >
        {/* Left column: sticky */}
        <div
          className="relative overflow-hidden"
          style={{
            position: "sticky",
            top: 0,
            height: "100vh",
            alignSelf: "start",
          }}
        >
          <LeftPanel />
        </div>

        {/* Right column: cards scroll through */}
        <div className="bg-cream relative">
          <DotMatrixAccent position="top-right" size="lg" />
          <div
            style={{
              padding: "var(--space-section-top) var(--pad-lg) var(--space-section-bottom)",
              display: "flex",
              flexDirection: "column",
              gap: "var(--gap-sm)",
            }}
          >
            {PILLARS.map((pillar) => (
              <PillarCard key={pillar.number} pillar={pillar} />
            ))}
          </div>
        </div>
      </div>

      {/* Mobile: stacked */}
      <div className="lg:hidden">
        <div className="relative overflow-hidden" style={{ minHeight: "50vh" }}>
          <LeftPanel />
        </div>
        <div
          className="bg-cream relative"
          style={{ padding: "var(--pad-section) var(--pad-container)" }}
        >
          <DotMatrixAccent position="top-right" size="lg" />
          <div className="flex flex-col" style={{ gap: "var(--gap-sm)" }}>
            {PILLARS.map((pillar, i) => (
              <BlurFade key={pillar.number} inView delay={i * 0.1}>
                <PillarCard pillar={pillar} />
              </BlurFade>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── Left panel: cream bg with GridPattern fading from top-left (matches TeamSectionV2) ── */

function LeftPanel() {
  return (
    <div className="relative h-full bg-cream overflow-hidden">
      {/* Architectural grid — visible upper-left, fades toward bottom-right.
          Matches the background effect used on TeamSectionV2 ("Boutique team"). */}
      <GridPattern
        width={24}
        height={24}
        x={-1}
        y={-1}
        className={cn(
          "z-[1] fill-transparent stroke-[hsla(60,3%,8%,0.10)]",
          "[mask-image:linear-gradient(to_bottom_right,rgba(0,0,0,0.6)_0%,rgba(0,0,0,0.25)_40%,transparent_70%)]",
        )}
      />

      {/* Content */}
      <div
        className="relative z-[2] flex flex-col justify-center h-full"
        style={{ padding: "var(--pad-section)" }}
      >
        <BlurFade inView delay={0.1}>
          <div style={{ maxWidth: "var(--max-w-heading)" }}>
            <h2
              className="type-h1 italic uppercase"
              style={{ color: "var(--charcoal)" }}
            >
              explore our services
            </h2>
          </div>
        </BlurFade>

        <BlurFade inView delay={0.25}>
          <div style={{ maxWidth: "var(--max-w-text)" }}>
            <p
              className="type-body"
              style={{
                color: "var(--charcoal)",
                opacity: 0.75,
                marginTop: "var(--space-gap-sm)",
              }}
            >
              We're in the business of ROAS, not reports. AI handles the
              tedium. Humans handle the moves that matter. Your CFO will
              notice.
            </p>
          </div>
        </BlurFade>

        <BlurFade inView delay={0.4}>
          <div
            className="flex flex-wrap items-center"
            style={{ gap: "var(--gap-xs)", marginTop: "var(--space-gap-md)" }}
          >
            <a href="#contact">
              <button className="btn btn-cta btn-lg">get a proposal</button>
            </a>
          </div>
        </BlurFade>
      </div>
    </div>
  );
}

/* ── Pillar card ─────────────────────────────────────────────── */

function PillarCard({ pillar }: { pillar: Pillar }) {
  return (
    <div
      className="relative flex flex-col"
      style={{ border: "var(--border-light-heavy)" }}
    >
      <CornerMarks dark={false} />

      {/* Card content */}
      <div style={{ padding: "var(--pad-lg)" }}>
        {/* Card header: number ── divider ── icon */}
        <div
          className="flex items-center"
          style={{ gap: "var(--gap-xs)", marginBottom: "var(--space-gap-md)" }}
        >
          <span
            className="type-stat flex-shrink-0"
            style={{ color: "var(--charcoal-14)", lineHeight: 1 }}
          >
            {pillar.number}
          </span>

          <div
            className="flex-1"
            style={{ borderTop: "var(--border-light)" }}
          />

          <div
            className="flex-shrink-0 bg-cream"
            style={{ width: 80, height: 80, isolation: "isolate" }}
          >
            <img
              src={pillar.icon}
              alt=""
              aria-hidden="true"
              className="w-full h-full object-contain"
              style={{ mixBlendMode: "darken" }}
              width={80}
              height={80}
            />
          </div>
        </div>

        {/* Title */}
        <h3
          className="type-h4 italic uppercase"
          style={{ color: "var(--charcoal)" }}
        >
          {pillar.heading}
        </h3>

        {/* Description */}
        <p
          className="type-body"
          style={{
            color: "var(--charcoal)",
            marginTop: "var(--space-gap-sm)",
          }}
        >
          {pillar.description}
        </p>

        {/* 2-column bullet grid */}
        <div
          className="grid grid-cols-1 sm:grid-cols-2"
          style={{ gap: "var(--space-gap-sm)", marginTop: "var(--space-gap-md)" }}
        >
          {pillar.bullets.map((bullet) => (
            <div key={bullet} className="flex items-start" style={{ gap: 6 }}>
              <span
                className="type-nav flex-shrink-0"
                style={{ color: "var(--terra)", marginTop: 2 }}
              >
                +
              </span>
              <span
                className="type-body"
                style={{ color: "var(--charcoal)" }}
              >
                {bullet}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Button bar — flush bottom, full width. Stacked on mobile, side-by-side on desktop */}
      <div
        className="flex flex-col sm:flex-row mt-auto"
        style={{ borderTop: "var(--border-light-heavy)" }}
      >
        {pillar.links.map((link, i) => (
          <Fragment key={link.href}>
            {i > 0 && (
              <div className="hidden sm:block" style={{ width: 1, backgroundColor: "var(--cream)" }} />
            )}
            <Link href={link.href} className="flex-1">
              <button className="btn btn-primary btn-lg w-full">
                {link.label}
              </button>
            </Link>
          </Fragment>
        ))}
      </div>
    </div>
  );
}
